import logger from '@/utils/logger';

import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A dynamic import that failed because the chunk is no longer on the server.
 *
 * Route chunks are content-hashed, so a deploy replaces them. A tab left open
 * across one still holds the old index, and the next navigation asks for a file
 * that now 404s. Browsers word this differently — Chrome and Safari talk about
 * fetching a dynamically imported module, Firefox about a module script — so
 * the match is deliberately loose. Guessing wrong in this direction only costs
 * a reload prompt where a generic error would have done.
 */
const isStaleChunkError = (error: Error): boolean =>
  /dynamically imported module|module script failed|ChunkLoadError|Importing a module/i.test(
    `${error.name} ${error.message}`,
  );

type Props = { children: ReactNode };
type State = { error: Error | null };

/**
 * Catches render errors below it, including the ones React.lazy throws.
 *
 * Without a boundary above Suspense a failed route import unmounts the whole
 * tree to a blank page — no message, no way back except a manual reload the
 * user has no reason to think of. That failure arrived with route splitting:
 * before it, every route was already in the entry chunk, so a tab open across a
 * deploy kept working. Now the first navigation after a deploy is the one that
 * breaks, which makes it most likely to hit someone right after a release.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error(
      {
        err: error,
        staleChunk: isStaleChunkError(error),
        componentStack: info.componentStack,
      },
      // The spec harness watches the browser console for this exact message to
      // fail a scenario at the point a route crashes (see the Before hook in
      // spec/src/steps/common.steps.ts). Without it a caught error is silent:
      // the boundary handles the exception, so no `pageerror` fires, and the
      // suite only notices minutes later when a locator stops matching.
      'Route failed to render',
    );
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // A stale chunk is not an error the user can act on except by reloading,
    // and reloading genuinely fixes it — so say that, rather than showing a
    // failure they will read as data loss.
    const stale = isStaleChunkError(error);

    // Deliberately plain elements rather than Mantine components. This renders
    // *because* something below it failed, and the failure can be anything in
    // the tree — reaching back into the component library to draw the apology
    // risks the fallback throwing too, and a boundary that cannot render is the
    // blank page it exists to prevent.
    return (
      <div
        role="alert"
        style={{ padding: '4rem 1rem', textAlign: 'center', maxWidth: '32rem', margin: '0 auto' }}
      >
        <h2>{stale ? 'A new version is available' : 'Something went wrong'}</h2>
        <p>
          {stale
            ? 'This page was updated while your tab was open. Reload to pick up the latest version.'
            : 'This page failed to load. Reloading may help; if it keeps happening, let us know.'}
        </p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
